import dayjs from "dayjs";
import { BACKEND_DATE_FORMAT } from "../../../consts/index.js";

const toISO = (d) => dayjs(d).format(BACKEND_DATE_FORMAT);

const inWindow = (iso, startISO, endISO) =>
  !dayjs(iso).isBefore(dayjs(startISO), "day") &&
  !dayjs(iso).isAfter(dayjs(endISO), "day");

const getBayName = (row) =>
  String(
    row.bayId ??
      row.greenhouseId ??
      row.plotId ??
      row.bayName ??
      row.greenhouseName ??
      "",
  );

const getRowName = (row) =>
  Number.isFinite(row.rowNumber)
    ? `${row.rowNumber}`
    : String(row.rowNumber ?? row.rowLabel);

const getUniqueKey = (row) =>
  `${getBayName(row)}:${
    Number.isFinite(row.rowNumber) ? row.rowNumber : getRowName(row)
  }`;

/** Strict: returns {type,value} or null */
const tasksPerDate = (dateISO, visibleDaysConfig = {}) => {
  const dayName = dayjs(dateISO).format("dddd").toLowerCase();
  for (const [k, v] of Object.entries(visibleDaysConfig)) {
    if (String(k).toLowerCase() !== dayName) continue;
    if (
      v &&
      typeof v === "object" &&
      v.type != null &&
      v.value != null &&
      Number.isFinite(Number(v.value))
    ) {
      return { type: String(v.type).toLowerCase(), value: Number(v.value) };
    }
    return null; // invalid entry
  }
  return null; // not found
};

/** Build: date -> [{ key, bayName, rowName, size, dateISO, isHarvested, rowLength }] */
const indexByDate = (rows, { startISO, endISO }) => {
  const map = new Map();
  for (const row of rows || []) {
    const bayName = getBayName(row);
    const rowName = getRowName(row);
    const key = getUniqueKey(row);
    const rowLength = Number(row.rowLength) || 0;
    const variety = row.variety;

    for (const p of row.points || []) {
      const dateISO = toISO(p.date);
      if (!inWindow(dateISO, startISO, endISO)) continue;
      const size = Number(p.avgLeafCm);
      if (!Number.isFinite(size)) continue;

      if (!map.has(dateISO)) map.set(dateISO, []);
      map.get(dateISO).push({
        key,
        bayName,
        rowName,
        rowLabel: row.rowLabel,
        size,
        dateISO,
        isHarvested: !!p.isHarvested,
        rowLength,
        variety: variety,
      });
    }
  }
  // sort each date descending by size (helps later)
  for (const [, arr] of map) arr.sort((a, b) => b.size - a.size);
  return map;
};

function readHarvestConfig(siteMetadata) {
  const cfg = siteMetadata?.HARVEST_OPTIMIZER || {};
  return {
    yieldPerM: Number(cfg.YIELD_PER_METRE_KG_PER_M) || 0,
    cratesPerPallet: Number(cfg.CRATES_PER_PALLET) || 0,
    weightPerCrate: Number(cfg.WEIGHT_PER_CRATE_KG) || 0,
  };
}

function wantToKg(wantObj, cfg) {
  const t = (wantObj.type || "").toLowerCase();
  const v = Number(wantObj.value) || 0;
  if (t === "pallet") return v * cfg.cratesPerPallet * cfg.weightPerCrate;
  if (t === "crate") return v * cfg.weightPerCrate;
  if (t === "total_yield") return v;
  return NaN; // not a kg-based target
}

function rowKg(item, cfg) {
  const len = Number(item.rowLength) || 0;
  return len > 0 ? len * cfg.yieldPerM : 0;
}

export const harvestAllocatorService = {
  /**
   * @param {{
   *   updatedUnharvestedData: Array<{
   *     bayId: string|number,
   *     rowLabel?: string,
   *     rowNumber?: number,
   *     rowLength?: number,
   *     variety?: number,
   *     points: Array<{date: string, isHarvested: boolean, avgLeafCm: number|null}>
   *   }>,
   *   harvestedData: Array<any>,
   *   dateWindow: { startISO: string, endISO: string },
   *   selectedDate: string,                      // ISO YYYY-MM-DD
   *   visibleDaysConfig: Record<string, number>, // e.g. { Mon: {type:'row', value:5}, ... }
   *   siteMetadata?: any,                        // for unit conversions and yield calc
   *   enableTimeMachineForPastDates?: boolean
   * }} args
   * @returns {Array<{
   *   recommendedHarvestDate: string,
   *   bayName: string,
   *   rowName: string,
   *   rowLabel: string,
   *   actualHarvestDate?: string,
   *   isHarvested?: boolean,
   *   optimalHarvestDate?: string,
   *   averageLeafLength?: string,
   *   rowLength?: number,
   *   variety?: number,
   *   totalYieldKg?: number
   * }>}
   */
  getFinalData({
    updatedUnharvestedData,
    harvestedData,
    dateWindow,
    selectedDate,
    visibleDaysConfig,
    siteMetadata,
    enableTimeMachineForPastDates,
  }) {
    const { startISO, endISO } = dateWindow || {};
    if (!startISO || !endISO) return [];

    const cfg = readHarvestConfig(siteMetadata);
    const selISO = toISO(selectedDate);
    const todayISO = dayjs().format(BACKEND_DATE_FORMAT);

    const used = new Set();
    const out = [];

    const finalize = () => {
      // Stable sort for UI
      out.sort((a, b) => {
        if (a.recommendedHarvestDate !== b.recommendedHarvestDate)
          return a.recommendedHarvestDate < b.recommendedHarvestDate ? -1 : 1;
        if (a.bayName !== b.bayName) return a.bayName < b.bayName ? -1 : 1;
        return a.rowName < b.rowName ? -1 : a.rowName > b.rowName ? 1 : 0;
      });
      return out;
    };

    // Index inputs
    const idxHarvested = indexByDate(harvestedData || [], { startISO, endISO });
    const idxUnharvested = indexByDate(updatedUnharvestedData || [], {
      startISO,
      endISO,
    });

    // Past: include harvested
    // - TimeMachine TRUE: stop at selectedDate
    // - TimeMachine FALSE: include harvested up to min(endISO, todayISO)
    const pastEndISO =
      enableTimeMachineForPastDates === true
        ? selISO
        : dayjs(endISO, BACKEND_DATE_FORMAT, true).isAfter(
              dayjs(todayISO, BACKEND_DATE_FORMAT, true),
              "day",
            )
          ? todayISO
          : endISO;

    for (
      let cur = dayjs(startISO).startOf("day");
      !cur.isAfter(dayjs(endISO), "day");
      cur = cur.add(1, "day")
    ) {
      const dISO = cur.format(BACKEND_DATE_FORMAT);
      if (dayjs(dISO).isAfter(dayjs(pastEndISO), "day")) break;

      const done = idxHarvested.get(dISO) || [];
      for (const item of done) {
        if (item.isHarvested !== true) continue;
        if (used.has(item.key)) continue;
        used.add(item.key);

        out.push({
          recommendedHarvestDate: dISO,
          bayName: item.bayName,
          rowName: item.rowName,
          rowLabel: item.rowLabel,
          actualHarvestDate: dISO,
          isHarvested: true,
          averageLeafLength: item.size.toFixed(1),
          rowLength: Number(item.rowLength) || 0,
          totalYieldKg: rowKg(item, cfg),
          variety: item.variety,
        });
      }
    }

    // Future: global list from selectedDate (TimeMachine TRUE)
    // OR from today (TimeMachine FALSE)
    const futureStartISO =
      enableTimeMachineForPastDates === true
        ? selISO
        : dayjs(todayISO, BACKEND_DATE_FORMAT, true).isAfter(
              dayjs(startISO, BACKEND_DATE_FORMAT, true),
              "day",
            )
          ? todayISO
          : startISO;

    // Guard clause (no empty if, no nested block)
    if (dayjs(futureStartISO).isAfter(dayjs(endISO), "day")) {
      return finalize();
    }

    // Future: global list from futureStartISO
    let globalCandidates = (idxUnharvested.get(futureStartISO) || []).filter(
      (c) => !c.isHarvested,
    );
    let ptr = 0;

    for (
      let cur = dayjs(futureStartISO).startOf("day");
      !cur.isAfter(dayjs(endISO), "day");
      cur = cur.add(1, "day")
    ) {
      const dISO = cur.format(BACKEND_DATE_FORMAT);

      const wantObj = tasksPerDate(dISO, visibleDaysConfig);
      if (!wantObj) continue; // invalid or missing

      if (wantObj.type === "row") {
        let want = Number(wantObj.value);
        if (!Number.isFinite(want) || want <= 0) continue;

        const alreadyHarvested = (idxHarvested.get(dISO) || []).length;
        want -= alreadyHarvested;
        if (want <= 0) continue;

        let remaining = want;
        while (remaining > 0 && ptr < globalCandidates.length) {
          const cand = globalCandidates[ptr++];
          if (used.has(cand.key)) continue; // skip already taken (harvested earlier or scheduled on a previous future day)
          used.add(cand.key);

          out.push({
            recommendedHarvestDate: dISO,
            bayName: cand.bayName,
            rowName: cand.rowName,
            rowLabel: cand.rowLabel,
            averageLeafLength: cand.size.toFixed(1),
            rowLength: Number(cand.rowLength) || 0,
            totalYieldKg: rowKg(cand, cfg),
            variety: cand.variety,
          });
          remaining -= 1;
        }
        // if we run out of candidates, we simply stop filling further days
        if (ptr >= globalCandidates.length) break;
        continue;
      }

      // KG-BASED MODES
      let targetKg = wantToKg(wantObj, cfg);
      if (!Number.isFinite(targetKg) || targetKg <= 0) continue;

      // Subtract any rows already harvested on that date (if any)
      const harvestedToday = idxHarvested.get(dISO) || [];
      if (harvestedToday.length) {
        const harvestedKg = harvestedToday
          .map((it) => rowKg(it, cfg))
          .reduce((a, b) => a + b, 0);
        targetKg -= harvestedKg;
      }
      if (targetKg <= 0) continue;

      let remainingKg = targetKg;
      while (remainingKg > 0 && ptr < globalCandidates.length) {
        const cand = globalCandidates[ptr++];
        if (used.has(cand.key)) continue;

        const perRowKg = rowKg(cand, cfg);
        if (perRowKg <= 0) {
          // zero-yield rows don't help; skip but still mark used so we don't loop forever
          used.add(cand.key);
          continue;
        }

        used.add(cand.key);
        out.push({
          recommendedHarvestDate: dISO,
          bayName: cand.bayName,
          rowName: cand.rowName,
          rowLabel: cand.rowLabel,
          averageLeafLength: cand.size.toFixed(1),
          rowLength: Number(cand.rowLength) || 0,
          totalYieldKg: perRowKg,
          variety: cand.variety,
        });
        remainingKg -= perRowKg;
      }
      if (ptr >= globalCandidates.length) break;
    }

    return finalize();
  },
};
